import React, { useEffect, useMemo, useState } from "react";
import CTabs, { CTabPanel } from "../../components/CTabs";
import CFormProvider from "../../components/HookFormComponents/CFormProvider";
import CFormTextField from "../../components/HookFormComponents/CFormTextField";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { CFormSelect } from "../../components/HookFormComponents/CFormSelect";
import CButton from "../../components/CButton";
import { useDispatch } from "react-redux";
import {
  BBBEE_LEVELS,
  statuses,
  SYSTEM_APPROACHES,
} from "../../constant/constants";
import {
  addCompany,
  getCompanyById,
  getCompanyByRegistrationNumber,
  updateCompany,
  deleteCompany,
} from "../../slices/companySlice";
import {
  addSdfUser,
  checkSdfEmailExists,
  fetchCompanyAdminList,
  getSdfUserById,
  getSdfUserByCompanyId,
  updateSdfUser,
} from "../../slices/sdfSlice";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { messages } from "../../constant/alertMessages";
import { setSdfUserData } from "../../utils/roleUtils";
import CFormPhoneNumberField from "../../components/HookFormComponents/CFormPhoneNumberField ";
import { isValidPhoneNumber } from "react-phone-number-input";
import { fetchAllSetaTemplates } from "../../slices/setaTemplateSlice";
import { getLocalData } from "../../constant/utils/commonUtils";
import moment from "moment";
import CFormDatePicker from "../../components/HookFormComponents/CFormDatePicker";
import { stopLoading } from "../../slices/commonSlice";

// Helper function to validate and format dates
const validateAndFormatDate = (dateString) => {
  if (!dateString) return null;

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;

  return date.toISOString().split("T")[0];
};

export default function CompanySetup() {
  const [activeTab, setActiveTab] = useState("company-setup1");
  const [isSaving, setIsSaving] = useState(false);
  const [hasValidatedOnce, setHasValidatedOnce] = useState(false);
  const [registrationNumberConflict, setRegistrationNumberConflict] =
    useState(false);
  const [isFirstValidation, setIsFirstValidation] = useState(true);
  const [validationTriggered, setValidationTriggered] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const view = searchParams.get("view");
  const queryCompanyId = searchParams.get("companyId");
  const [setaTemplates, setSetaTemplates] = useState([]);
  const [oldEmail, setOldEmail] = useState("");
  const [companySdfList, setCompanySdfList] = useState([]);
  const [selectedSdfUserId, setSelectedSdfUserId] = useState(null);

  const [details, setDetails] = useState({
    company_details: {},
    contact_person_details: {},
  });

  const sdfUser = useMemo(
    () => JSON.parse(localStorage.getItem("sdfUser")),
    []
  );
  const localStorageCompanyId = JSON.parse(
    localStorage.getItem("sdfUser")
  )?.company_id;
  useEffect(() => {
    if (view === "profile") {
      setActiveTab("company-setup2");
    } else if (view === "company") {
      setActiveTab("company-setup1");
    } else {
      // console.log("No view parameter - showing both tabs with default tab1");
    }
  }, [view]);

  useEffect(() => {
    dispatch(fetchAllSetaTemplates()).then((res) => {
      if (res.payload && Array.isArray(res.payload)) {
        const filterData = res.payload.filter((item) => {
          return (
            item.file_structure &&
            Object.keys(item.file_structure).length > 0 &&
            item.status !== statuses.INACTIVE
          );
        });
        setSetaTemplates(filterData);
      }
    });
  }, [dispatch]);

  const tabs = [
    { label: "1", value: "company-setup1" },
    { label: "2", value: "company-setup2" },
  ];

  // Validation Schema
  const CompanySchema = Yup.object().shape({
    registered_name: Yup.string().required("Company Name is required"),
    trading_name: Yup.string().required("Trading Name is required"),
    company_registration_number: Yup.string()
      .required("Company Registration Number is required")
      .length(14, "Company Registration Number must be exactly 14 characters")
      .matches(
        /^[a-zA-Z0-9\s\-_\/]+$/,
        "Only letters, numbers, hyphens, underscores, and forward slashes are allowed"
      ),
    company_registration_date: Yup.string()
      .required("Company Registration Date is required")
      .test("is-valid-date", "Please enter a valid date", (value) => {
        if (!value) return false;

        // Handle different date formats that might come from the date picker
        let momentDate = null;

        // Try to parse as DD/MM/YYYY format
        if (typeof value === "string" && value.includes("/")) {
          momentDate = moment(value, "DD/MM/YYYY", true);
        }

        // If that fails, try to parse as Date object or ISO string
        if (!momentDate || !momentDate.isValid()) {
          momentDate = moment(value);
        }

        return momentDate.isValid();
      })
      .test(
        "is-not-future",
        "Registration date cannot be in the future",
        (value) => {
          if (!value) return true;

          let momentDate = null;

          // Try to parse as DD/MM/YYYY format
          if (typeof value === "string" && value.includes("/")) {
            momentDate = moment(value, "DD/MM/YYYY", true);
          }

          // If that fails, try to parse as Date object or ISO string
          if (!momentDate || !momentDate.isValid()) {
            momentDate = moment(value);
          }

          if (!momentDate.isValid()) return true; // Let other validation handle invalid dates

          const today = moment().endOf("day");
          return momentDate.isSameOrBefore(today);
        }
      ),
    telephone_number: Yup.string()
      .required("Telephone Number is required")
      .test("is-valid-phone", "Invalid phone number", (value) => {
        if (!value) return false;
        return isValidPhoneNumber(value);
      }),
    address: Yup.string().required("Address is required"),
    website_url: Yup.string(),
    b_bbee: Yup.string().required("B-BBEE Level is required"),
    seta_id: Yup.string().required("SETA ID is required"),
    system_approach: Yup.string().required(
      "Plan Generation Option is required"
    ),
    // Contact Person Details (Tab 2)
    first_name: Yup.string()
      .required("First Name is required")
      .matches(/^[a-zA-Z\s]+$/, "Only letters and spaces are allowed"),
    last_name: Yup.string()
      .required("Last Name is required")
      .matches(/^[a-zA-Z\s]+$/, "Only letters and spaces are allowed"),
    title: Yup.string()
      .required("Position/Title is required")
      .matches(/^[a-zA-Z\s]+$/, "Only letters and spaces are allowed"),
    contact_number: Yup.string()
      .required("Contact Number is required")
      .test("is-valid-phone", "Invalid phone number", (value) => {
        if (!value) return false;
        return isValidPhoneNumber(value);
      }),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email Address is required"),
    registration_number: Yup.string()
      .required("SDF Registration Number is required")
      .max(20, "SDF Registration Number must not exceed 20 characters")
      .matches(
        /^[a-zA-Z0-9]+$/,
        "SDF Registration Number must contain only letters and numbers"
      ),
    IDNumber: Yup.string()
      .required("ID Number is required")
      .min(4, "ID Number must be at least 4 characters")
      .max(6, "ID Number must not exceed 6 characters")
      .matches(
        /^[a-zA-Z0-9]+$/,
        "ID Number must contain only letters and numbers"
      ),
  });

  const methods = useForm({
    resolver: yupResolver(CompanySchema),
    mode: "onChange",
    defaultValues: {
      registered_name: "",
      trading_name: "",
      company_registration_number: "",
      company_registration_date: "",
      telephone_number: "",
      address: "",
      website_url: "",
      b_bbee: "",
      seta_id: "",
      system_approach: "structured_with_training_selection", // Default to Employee Selected
      // Contact Person Details (Tab 2)
      first_name: "",
      last_name: "",
      IDNumber: "",
      title: "",
      contact_number: "",
      email: "",
      registration_number: "",
    },
  });

  // Watch form values for debugging
  const watchedValues = methods.watch();
  // Handle registration number change
  const handleRegistrationNumberChange = (value) => {
    if (registrationNumberConflict) {
      setRegistrationNumberConflict(false);
    }

    // Trigger validation for this field to clear any inline error messages
    if (value && value.trim() !== "") {
      methods.trigger("company_registration_number");
    }
  };

  // Handle date change with validation for dd/mm/yy format
  const applySdfProfileToForm = (userData) => {
    if (!userData) return;
    methods.setValue("first_name", userData.first_name || "");
    methods.setValue("last_name", userData.last_name || "");
    methods.setValue("title", userData.title || "");
    methods.setValue("contact_number", userData.contact_number || "");
    methods.setValue("email", userData.email || "");
    methods.setValue(
      "registration_number",
      userData.sdf_registration_number || "",
    );
    methods.setValue("IDNumber", userData.id_number || "");
    setOldEmail(userData.email || "");
    setSelectedSdfUserId(userData.id || userData.company_user_id);
  };

  const handleDateChange = (value, fieldName) => {
    if (value) {
      // The CFormDatePicker now handles dd/mm/yy formatting automatically
      // This function is kept for backward compatibility but the date picker handles formatting
      methods.setValue(fieldName, value);
      methods.trigger(fieldName);
    } else {
      methods.setValue(fieldName, "");
      methods.trigger(fieldName);
    }
  };

  // Log component state
  useEffect(() => {
    const loadData = async () => {
      const dataToSet = {};

      // Use localStorage companyId directly for loading data
      const targetCompanyId = queryCompanyId || localStorageCompanyId;

      if (targetCompanyId) {
        // Load company data
        const companyRes = await dispatch(getCompanyById(targetCompanyId));
        if (companyRes.payload) {
          const companyData = companyRes.payload;
          dataToSet.registered_name = companyData.registered_name || "";
          dataToSet.trading_name = companyData.trading_name || "";
          dataToSet.company_registration_number =
            companyData.company_registration_number || "";
          if (companyData.company_registration_date) {
            // Convert timestamp to dd/mm/yy format for display
            const date = new Date(companyData.company_registration_date);
            if (!isNaN(date.getTime())) {
              dataToSet.company_registration_date = moment(date).toISOString();
            } else {
              console.warn(
                "Invalid company registration date:",
                companyData.company_registration_date
              );
            }
          }
          dataToSet.telephone_number = companyData.telephone_number || "";
          dataToSet.address = companyData.address || "";
          dataToSet.website_url = companyData.website_url || "";
          dataToSet.b_bbee = companyData.b_bbee || "";
          dataToSet.seta_id = companyData.seta_id || "";
          dataToSet.system_approach = companyData.system_approach || "";
        }

        // Load contact person profile
        try {
          let userData = null;

          if (view === "profile" && sdfUser?.id) {
            // Profile view: load the logged-in user's own record (company_admin or assigned SDF)
            const profileRes = await dispatch(getSdfUserById(sdfUser.id));
            if (profileRes.payload) {
              userData = profileRes.payload;
            }
            setCompanySdfList([]);
          } else {
            // Company setup: load company admin profile(s) for this company
            const adminListRes = await dispatch(
              fetchCompanyAdminList(targetCompanyId),
            );
            let adminList = Array.isArray(adminListRes.payload)
              ? adminListRes.payload
              : [];
            if (adminList.length === 0) {
              const fallbackRes = await dispatch(
                getSdfUserByCompanyId(targetCompanyId),
              );
              if (fallbackRes.payload) {
                adminList = [fallbackRes.payload];
              }
            }
            setCompanySdfList(adminList);
            userData =
              adminList.find((admin) => admin.id === selectedSdfUserId) ||
              adminList[0] ||
              null;
          }

          if (userData) {
            dataToSet.first_name = userData.first_name || "";
            dataToSet.last_name = userData.last_name || "";
            dataToSet.title = userData.title || "";
            dataToSet.contact_number = userData.contact_number || "";
            dataToSet.email = userData.email || "";
            dataToSet.registration_number = userData.sdf_registration_number;
            dataToSet.IDNumber = userData.id_number || "";
            setOldEmail(userData.email);
            setSelectedSdfUserId(userData.id || userData.company_user_id);
          }
        } catch (error) {
          // No company admin found — continue with company-only fields
        }
      }

      const existingValues = methods.getValues();
      const cleanDataToSet = Object.fromEntries(
        Object.entries(dataToSet)
          .filter(([_, value]) => value !== undefined && value !== null)
          .map(([key, value]) => [key, String(value)])
      );
      methods.reset(cleanDataToSet);
    };

    loadData();
  }, [dispatch, methods, sdfUser, queryCompanyId, view]);

  // Function to validate all required fields and show specific error messages
  const validateAllRequiredFields = async () => {
    // Set validation triggered state
    setValidationTriggered(true);

    // Trigger validation for all fields
    const isValid = await methods.trigger();

    if (!isValid) {
      // Find which tab has errors and switch to it
      const tab1Fields = [
        "registered_name",
        "trading_name",
        "company_registration_number",
        "company_registration_date",
        "telephone_number",
        "address",
        "b_bbee",
        "seta_id",
        "system_approach",
      ];

      const errors = methods.formState.errors;
      const hasTab1Errors = tab1Fields.some((field) => errors[field]);

      if (hasTab1Errors) {
        setActiveTab("company-setup1");
      } else {
        setActiveTab("company-setup2");
      }

      return false;
    }

    return true;
  };

  // Function to validate tab fields and show errors
  const validateTabFields = async (tabName) => {
    const tab1Fields = [
      "registered_name",
      "trading_name",
      "company_registration_number",
      "company_registration_date",
      "telephone_number",
      "address",
      "b_bbee",
      "seta_id",
      "system_approach",
    ];
    const tab2Fields = [
      "first_name",
      "last_name",
      "title",
      "contact_number",
      "email",
      "registration_number",
      "IDNumber",
    ];

    const fieldsToValidate =
      tabName === "company-setup1" ? tab1Fields : tab2Fields;

    // Set validation triggered state
    setValidationTriggered(true);

    // Trigger validation for the specific tab fields
    const isValid = await methods.trigger(fieldsToValidate);

    if (!isValid) {
      // Form validation will automatically show inline errors
      return false;
    }

    // Check registration number existence for tab 1
    if (tabName === "company-setup1") {
      const registrationNumber = methods.getValues(
        "company_registration_number"
      );
      if (registrationNumber) {
        // Skip registration number check if we're updating the same company
        const targetCompanyId = queryCompanyId || sdfUser?.company_id;
        if (targetCompanyId) {
          // We're updating an existing company, so skip the conflict check
          setRegistrationNumberConflict(false);
        } else {
          // We're creating a new company, so check for conflicts
          try {
            const existingCompanyRes = await dispatch(
              getCompanyByRegistrationNumber(registrationNumber)
            );
            // If we get here, it means a company was found (no error thrown)
            if (existingCompanyRes.payload) {
              setRegistrationNumberConflict(true);
              toast.error(
                "Company with this registration number already exists. Please change the registration number and try again."
              );
              return false;
            }
          } catch (error) {
            // If error is thrown, it means no company was found (which is what we want)
            setRegistrationNumberConflict(false);
          }
        }
      }
    }

    return true;
  };

  // Handle tab change
  const handleTabChange = async (newTab) => {
    // Validate when moving from company tab to profile tab
    if (activeTab === "company-setup1" && newTab === "company-setup2") {
      const isValid = await validateTabFields("company-setup1");
      if (isValid) {
        setIsFirstValidation(false);
        setActiveTab(newTab);
      } else {
        // Stay on current tab if validation fails
        // console.log("Validation failed, staying on current tab");
      }
    }
    // Allow moving back from profile tab to company tab without validation
    else if (activeTab === "company-setup2" && newTab === "company-setup1") {
      setActiveTab(newTab);
    }
    // For any other tab changes, just switch tabs
    else {
      setActiveTab(newTab);
    }
  };

  const onSubmit = async (data) => {
    // Prevent multiple submissions
    if (isSaving) {
      return;
    }

    // Define fields for each tab for validation
    const companyFields = [
      "registered_name",
      "trading_name",
      "company_registration_number",
      "company_registration_date",
      "telephone_number",
      "address",
      "b_bbee",
      "seta_id",
      "system_approach",
    ];
    const profileFields = [
      "first_name",
      "last_name",
      "title",
      "contact_number",
      "email",
      "registration_number",
      "IDNumber",
    ];

    // Determine which fields to validate based on the current view
    let fieldsToValidate;
    if (view === "company") {
      fieldsToValidate = companyFields;
    } else if (view === "profile") {
      fieldsToValidate = profileFields;
    } else {
      // For full form (both company and profile), validate all fields
      fieldsToValidate = [...companyFields, ...profileFields];
    }

    // Trigger validation
    const isValid = await methods.trigger(fieldsToValidate);
    if (!isValid) {
      toast.error("Please fill all the required fields correctly.");
      return;
    }

    setIsSaving(true);
    try {
      // Use localStorage companyId directly
      const targetCompanyId = queryCompanyId || localStorageCompanyId;

      if (view === "company") {
        // --- Update Company Details Only ---
        const companyData = {
          registered_name: data.registered_name,
          trading_name: data.trading_name,
          company_registration_date: data.company_registration_date,
          company_registration_number: data.company_registration_number,
          telephone_number: data.telephone_number,
          address: data.address,
          website_url: data.website_url,
          b_bbee: data.b_bbee,
          seta_id: data.seta_id,
          system_approach: data.system_approach,
          // user_id belongs to company_user (SDF), not the company table — do not send here
        };
        const res = await dispatch(
          updateCompany({ companyId: targetCompanyId, ...companyData })
        );
        if (res.meta.requestStatus === "fulfilled") {
          toast.success("Company details updated successfully!");
        } else {
          toast.error("Failed to update company details.");
        }
      } else if (view === "profile") {
        // --- Update Profile Details Only ---
        try {
          const sdfUserId = sdfUser?.id || selectedSdfUserId;
          if (!sdfUserId) {
            toast.error("User profile not found. Cannot update profile.");
            return;
          }

          const profileRes = await dispatch(getSdfUserById(sdfUserId));
          const activeUser = profileRes.payload;
          if (!activeUser) {
            toast.error("User profile not found. Cannot update profile.");
            return;
          }

          const sdfUserData = {
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email,
            contact_number: data.contact_number,
            title: data.title,
            id_number: data.IDNumber,
            sdf_registration_number: data.registration_number,
            user_id: activeUser.user_id,
          };

          const res = await dispatch(
            updateSdfUser({ id: sdfUserId, updatedData: sdfUserData, oldEmail }),
          );
          if (res.meta.requestStatus === "fulfilled") {
            toast.success("Profile updated successfully!");
            // Update the localStorage with new user data
            const updatedUserData = { ...sdfUser, ...sdfUserData };
            setSdfUserData(updatedUserData);
          } else {
            const errorMsg =
              res?.payload?.message ||
              res?.error?.message ||
              "Failed to update profile.";
            toast.error(errorMsg);
          }
        } catch (error) {
          console.error("Profile update error:", error);
          toast.error("Failed to update profile. Please try again.");
        }
      } else {
        // --- Original Logic for Full Form (Create or Update Both) ---
        if (targetCompanyId) {
          // Update both company and user
          const companyData = {
            registered_name: data.registered_name,
            trading_name: data.trading_name,
            company_registration_date: data.company_registration_date,
            company_registration_number: data.company_registration_number,
            telephone_number: data.telephone_number,
            address: data.address,
            website_url: data.website_url,
            b_bbee: data.b_bbee,
            seta_id: data.seta_id,
            system_approach: data.system_approach,
            // user_id: sdfUser.user_id
          };
          await dispatch(
            updateCompany({ companyId: targetCompanyId, ...companyData })
          );

          // Get SDF user ID for this company and update it
          try {
            const adminListRes = await dispatch(
              fetchCompanyAdminList(targetCompanyId),
            );
            let adminList = Array.isArray(adminListRes.payload)
              ? adminListRes.payload
              : [];
            if (adminList.length === 0) {
              const fallbackRes = await dispatch(
                getSdfUserByCompanyId(targetCompanyId),
              );
              if (fallbackRes.payload) {
                adminList = [fallbackRes.payload];
              }
            }
            const activeAdmin =
              adminList.find((admin) => admin.id === selectedSdfUserId) ||
              adminList[0];

            if (activeAdmin) {
              const sdfUserId = activeAdmin.id || activeAdmin.company_user_id;
              const sdfUserData = {
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email,
                contact_number: data.contact_number,
                title: data.title,
                id_number: data.IDNumber,
                sdf_registration_number: data.registration_number,
                user_id: activeAdmin.user_id,
              };

              const updateSdfRes = await dispatch(
                updateSdfUser({
                  id: sdfUserId,
                  updatedData: sdfUserData,
                  oldEmail,
                }),
              );
              if (updateSdfRes.meta.requestStatus === "fulfilled") {
                toast.success("Company and Profile updated successfully!");
                // Update the localStorage with new user data if this is the current user
                if (sdfUser && localStorageCompanyId === targetCompanyId) {
                  const updatedUserData = { ...sdfUser, ...sdfUserData };
                  localStorage.setItem(
                    "sdfUser",
                    JSON.stringify(updatedUserData)
                  );
                }
                if (queryCompanyId) {
                  navigate("/company-list");
                }
                return;
              } else {
                const errorMsg =
                  updateSdfRes?.payload?.message ||
                  updateSdfRes?.error?.message ||
                  "Failed to update company admin.";
                toast.error(errorMsg);
                return;
              }
            } else {
              toast.error("Company admin not found for this company.");
              return;
            }
          } catch (error) {
            console.error("Error updating company admin:", error);
            toast.error("Failed to update company admin. Please try again.");
            return;
          }
        } else if (!queryCompanyId) {
          // Only create new company and user if NOT editing (no queryCompanyId)

          // First, validate email before creating anything
          if (data.email) {
            try {
              const emailCheckRes = await dispatch(
                checkSdfEmailExists(data.email)
              );
              if (emailCheckRes.payload) {
                toast.error(
                  "A user with this email address has already been registered."
                );
                setIsSaving(false);
                return;
              }
            } catch (error) {
              // Email is available
            }
          }

          const companyData = {
            registered_name: data.registered_name,
            trading_name: data.trading_name,
            company_registration_date: data.company_registration_date,
            company_registration_number: data.company_registration_number,
            telephone_number: data.telephone_number,
            address: data.address,
            website_url: data.website_url,
            b_bbee: data.b_bbee,
            seta_id: data.seta_id,
            system_approach: data.system_approach,
          };

          const companyRes = await dispatch(addCompany(companyData));
          if (companyRes.meta.requestStatus === "fulfilled") {
            const companyId = companyRes.payload.id;
            const sdfUserData = {
              first_name: data.first_name,
              last_name: data.last_name,
              email: data.email,
              contact_number: data.contact_number,
              title: data.title,
              company_id: companyId,
              id_number: data.IDNumber,
              sdf_registration_number: data.registration_number,
            };
            const sdfUserRes = await dispatch(addSdfUser(sdfUserData));
            if (sdfUserRes.meta.requestStatus === "fulfilled") {
              toast.success("Company and company admin added successfully!");
              navigate("/company-list");
            } else {
              // If SDF user creation fails, we need to delete the company that was just created
              try {
                await dispatch(deleteCompany(companyId));
              } catch (deleteError) {
                console.error(
                  "Failed to delete company after SDF user creation failure:",
                  deleteError
                );
              }

              // Show generic error message since email validation was already done
              const errorMsg =
                sdfUserRes?.payload?.message ||
                sdfUserRes?.error?.message ||
                "Failed to create company admin.";
              toast.error(errorMsg);
              throw new Error("Failed to create company admin.");
            }
          } else {
            toast.error("Failed to create company.");
            throw new Error("Failed to create company.");
          }
        } else {
          toast.error(
            "Invalid operation. Cannot create new company when editing."
          );
          throw new Error(
            "Invalid operation. Cannot create new company when editing."
          );
        }
      }
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsSaving(false);
      dispatch(stopLoading());
    }
  };

  return (
    <div>
      <h2 className="2xl:text-3xl 2xl:leading-3xl text-2xl leading-2xl text-blue-500 font-semibold 2xl:mb-4 md:mb-3 mb-4">
        {queryCompanyId
          ? "Edit Company"
          : view === "profile"
          ? "Profile"
          : view === "company"
          ? "Company Details"
          : "Company Setup"}
      </h2>
      {(queryCompanyId || !view) && (
        <CTabs
          tabs={tabs}
          value={activeTab}
          onChange={handleTabChange}
          className="mb-4 company-setup-tabs"
        />
      )}
      {(!view || view === "company") && (
        <CTabPanel value={activeTab} index="company-setup1">
          <div className="border border-blue-200 bg-white rounded-lg p-4 mb-0 relative mt-8 h-[calc(100vh-246px)]">
            <p className="bg-white text-lg font-semibold text-gray-700 mb-4 2xl:text-xl 2xl:leading-xl lg:text-base lg:leading-base text-sm leading-sm absolute top-[-16px] left-[16px] px-1">
              Company Details:
            </p>
            <CFormProvider
              onSubmit={methods.handleSubmit(onSubmit)}
              methods={methods}
              className="custom-input-group"
            >
              <div className="grid 2xl:grid-cols-3 grid-cols-2 gap-4">
                <CFormTextField
                  name="registered_name"
                  label="Company Name"
                  className="2xl:!mt-4 !mt-0"
                />
                <CFormTextField
                  name="trading_name"
                  label="Trading Name"
                  className="2xl:!mt-4 !mt-0"
                />
                <div className="relative 2xl:!mt-4 !mt-0">
                  <CFormTextField
                    name="company_registration_number"
                    label="Company Registration Number"
                    className={
                      registrationNumberConflict ? "border-red-500" : ""
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      methods.setValue("company_registration_number", value);
                      handleRegistrationNumberChange(value);

                      // Clear registration number conflict if user is typing
                      if (registrationNumberConflict) {
                        setRegistrationNumberConflict(false);
                      }
                    }}
                  />
                  {registrationNumberConflict && (
                    <p className="text-red-500 text-sm mt-1">
                      This registration number already exists. Please change it.
                    </p>
                  )}
                </div>
                <CFormDatePicker
                  name={`company_registration_date`}
                  className="2xl:!mt-0 !mt-0"
                  label="Company Registration Date"
                  disabled={false}
                  readOnly={true}
                  maxDate={moment().endOf("day").toDate()}
                />
                <CFormPhoneNumberField
                  name="telephone_number"
                  label="Telephone Number"
                  customClassName="custom-phone-input 2xl:!mt-0 !mt-0"
                />
                <CFormTextField
                  name="address"
                  label="Address"
                  className="2xl:!mt-0 !mt-0"
                />
                <CFormTextField
                  name="website_url"
                  label="Website URL (If Any)"
                  className="2xl:!mt-0 !mt-0"
                />

                <CFormSelect
                  name="b_bbee"
                  label="B-BBEE Level"
                  options={BBBEE_LEVELS.map((level) => ({
                    id: level.key,
                    value: level.value,
                  }))}
                />

                <CFormSelect
                  name="seta_id"
                  options={setaTemplates.map((template) => {
                    return { id: template.id, value: template.seta_name };
                  })}
                  label="SETA"
                />

                {view !== "company" && (
                  <CFormSelect
                    name="system_approach"
                    label="Plan Generation Option"
                    infoTooltip="(1) AI Generated – Uses AI to generate the Skills Plan based on collected employee needs data. (2) Employee Selected – Employees complete needs assessments and select training programmes, which managers then approve."
                    options={SYSTEM_APPROACHES.filter(
                      (approach) => approach.active
                    ).map((approach) => ({
                      id: approach.key,
                      value: approach.value,
                    }))}
                  />
                )}
              </div>
              <div className="flex justify-end gap-2 absolute bottom-4 right-4">
                {!view ? (
                  <CButton
                    type="button"
                    onClick={() => handleTabChange("company-setup2")}
                    className="min-w-[100px] bg-blue-500 text-white px-4 py-2 rounded-md border border-blue-500 border-solid"
                  >
                    Next
                  </CButton>
                ) : (
                  <CButton
                    type="submit"
                    disabled={isSaving}
                    className={`min-w-[100px] bg-blue-500 text-white px-4 py-2 rounded-md border border-blue-500 border-solid ${
                      isSaving
                        ? "bg-blue-400 border-blue-400 text-white cursor-not-allowed"
                        : "bg-blue-500 border-blue-500 text-white hover:bg-blue-600"
                    }`}
                  >
                    {isSaving ? (
                      <span className="flex items-center justify-center">
                        <span className="mr-2">
                          {sdfUser?.company_id ? "Updating..." : "Saving..."}
                        </span>
                        <svg
                          className="animate-spin h-5 w-5"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      </span>
                    ) : localStorageCompanyId ? (
                      "Update"
                    ) : (
                      "Save"
                    )}
                  </CButton>
                )}
              </div>
            </CFormProvider>
          </div>
        </CTabPanel>
      )}
      {(!view || view === "profile") && (
        <CTabPanel value={activeTab} index="company-setup2">
          <div className="border border-blue-200 bg-white rounded-lg p-4 mb-0 relative mt-8 h-[calc(100vh-246px)]">
            <p className="bg-white text-lg font-semibold text-gray-700 mb-4 2xl:text-xl 2xl:leading-xl lg:text-base lg:leading-base text-sm leading-sm absolute top-[-16px] left-[16px] px-1">
              Contact Person Details:
            </p>
            {view !== "profile" && companySdfList.length > 1 && (
              <div className="mb-4 mt-2 max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Company Admin Profile
                </label>
                <select
                  className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm"
                  value={selectedSdfUserId || ""}
                  onChange={(e) => {
                    const id = e.target.value;
                    const sdf = companySdfList.find(
                      (item) => String(item.id) === String(id),
                    );
                    applySdfProfileToForm(sdf);
                  }}
                >
                  {companySdfList.map((sdf) => (
                    <option key={sdf.id} value={sdf.id}>
                      {`${sdf.first_name || ""} ${sdf.last_name || ""}`.trim() ||
                        sdf.email ||
                        "Company Admin"}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <CFormProvider
              onSubmit={methods.handleSubmit(onSubmit)}
              methods={methods}
              className="custom-input-group"
            >
              <div className="grid 2xl:grid-cols-3 grid-cols-2 gap-4">
                <CFormTextField
                  name="first_name"
                  label="First Name"
                  className="2xl:!mt-4 !mt-0"
                />
                <CFormTextField
                  name="last_name"
                  label="Last Name"
                  className="2xl:!mt-4 !mt-0"
                />
                <CFormTextField
                  name="IDNumber"
                  label="ID Number"
                  className="2xl:!mt-4 !mt-0"
                />
                <CFormTextField
                  name="title"
                  label="Position/Title"
                  className="2xl:!mt-0 !mt-0"
                />
                <CFormPhoneNumberField
                  name="contact_number"
                  label="Contact Number"
                  customClassName="custom-phone-input 2xl:!mt-0 !mt-0"
                />
                <CFormTextField
                  name="email"
                  label="Email Address"
                  className="2xl:!mt-0 !mt-0"
                  // disabled={view === "profile"}
                />
                <CFormTextField
                  name="registration_number"
                  label="SDF Registration Number"
                  className="2xl:!mt-0 !mt-0"
                />
              </div>
              <div className="flex justify-end gap-2 absolute bottom-4 right-4">
                {!view && (
                  <CButton
                    type="button"
                    onClick={() => handleTabChange("company-setup1")}
                    className="min-w-[100px] bg-transparent text-blue-500 px-4 py-2 rounded-md border border-blue-500 border-solid"
                  >
                    Prev
                  </CButton>
                )}
                <CButton
                  type="submit"
                  disabled={isSaving}
                  className={`min-w-[100px] bg-blue-500 text-white px-4 py-2 rounded-md border border-blue-500 border-solid ${
                    isSaving
                      ? "bg-blue-400 border-blue-400 text-white cursor-not-allowed"
                      : "bg-blue-500 border-blue-500 text-white hover:bg-blue-600"
                  }`}
                >
                  {isSaving ? (
                    <span className="flex items-center justify-center">
                      <span className="mr-2">
                        {sdfUser?.company_id ? "Updating..." : "Saving..."}
                      </span>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    </span>
                  ) : localStorageCompanyId ? (
                    "Update"
                  ) : (
                    "Save"
                  )}
                </CButton>
              </div>
            </CFormProvider>
          </div>
        </CTabPanel>
      )}
    </div>
  );
}
